import React from "react";
import { Alert, App as AntdApp, Button, Checkbox, Dropdown, Empty, Modal, Popconfirm, Skeleton, Table, Tag, Tooltip, Typography, Upload } from "antd";
import { CheckOutlined, DownOutlined, ExportOutlined, FileExcelOutlined, SwapOutlined } from "@ant-design/icons";
import api from "@/api/axios.js";
import "./UploadExcelCodesModal.css";

const { Text } = Typography;

const MODAL_WIDTH = 960;
const PAGE_SIZE = 8;

const FORMAT_LABELS = {
	"standard": "Descrizione e codice",
	"padre-figlio": "Padre-figlio",
	"codice-singolo": "Un solo codice per riga",
	"solo-raggruppamento": "Solo raggruppamento"
};

const itemKey = (item) => String(item?.cardId);

// Nome della foto con evidenziato il numero da cui è arrivato il codice.
function PhotoName({ name, token })
{
	const text = String(name || "");
	const index = token ? text.indexOf(token) : -1;
	if (index < 0)
		return <span className="xc-photo-name">{text}</span>;
	return (
		<span className="xc-photo-name">
			{text.slice(0, index)}
			<mark className="xc-hit">{token}</mark>
			{text.slice(index + token.length)}
		</span>
	);
}

function PhotoLine({ photo })
{
	let outcome;
	if (photo.status === "matched")
		outcome = (
			<Tooltip title={photo.excelDescrizione ? `Excel: ${photo.excelDescrizione}` : null}>
				<span className="xc-code is-small">{photo.codice}</span>
			</Tooltip>
		);
	else if (photo.status === "unknown")
		outcome = <span className="xc-photo-miss">{photo.tokens.join(", ")} non presenti nell'Excel</span>;
	else
		outcome = <span className="xc-photo-none">nessun codice nel nome</span>;

	return (
		<div className={`xc-photo is-${photo.status}`}>
			<PhotoName name={photo.name} token={photo.token}/>
			{outcome}
		</div>
	);
}

const needsReview = (item) => !item.reviewed && Boolean(item.review);

// Vale solo per le card che avevano già un codice: si mostra lì (nota sopra l'elenco e
// suggerimento sul riquadro), non più come piè di pagina di tutta la finestra.
const ALREADY_SET_NOTE = "L'import non sovrascrive i codici già presenti: seleziona quelli da sostituire con il codice dell'Excel.";

// Card "già con codice" dove l'Excel propone un codice diverso: le uniche selezionabili.
const isReplaceable = (item) => Boolean(item?.codice) && item.codice !== item.codiceAttuale;

function buildTiles(report, mode)
{
	const assigned = report.assigned || [];
	const tiles = [
		{ key: "assigned", label: mode === "preview" ? "Da assegnare" : "Assegnati", items: assigned, tone: "success" },
		{ key: "toReview", label: "Da ricontrollare", items: assigned.filter(needsReview), tone: "warning" },
		{ key: "notFound", label: "Foto senza codice", items: report.notFound || [], tone: "error" },
		{ key: "noPhoto", label: "Senza foto", items: report.noPhoto || [], tone: "neutral" }
	];
	if (report.alreadySet?.length)
		tiles.push({ key: "alreadySet", label: "Già con codice", items: report.alreadySet, tone: "neutral", hint: ALREADY_SET_NOTE });
	return tiles;
}

function defaultTileKey(tiles)
{
	for (const key of ["toReview", "notFound", "assigned"])
	{
		const tile = tiles.find((t) => t.key === key);
		if (tile?.items.length)
			return key;
	}
	return "assigned";
}

function CodeChip({ code, muted })
{
	if (!code)
		return null;
	return <span className={`xc-code${muted ? " is-muted" : ""}`}>{code}</span>;
}

function ProductCell({ item })
{
	return (
		<div className="xc-product">
			<div className="xc-product-name">{item.descrizione || <Text type="secondary">Descrizione non estratta</Text>}</div>
			{item.photo
				? (
					<div className="xc-photos">
						<PhotoLine photo={item.photo}/>
						{item.otherPhotos > 0 && (
							<span className="xc-photo-none">
								{item.otherPhotos === 1 ? "1 altra foto non considerata" : `${item.otherPhotos} altre foto non considerate`}
							</span>
						)}
					</div>
				)
				: <div className="xc-photo-none">Nessuna foto prodotto sulla card</div>}
		</div>
	);
}

function ImportReport({ result, mode, onApplyCode, onMarkReviewed, onOpenCard, savingKey, selectedIds = [], onSelectIds, onBulkReplace, bulkReplacing })
{
	const report = result?.report || {};
	const tiles = React.useMemo(() => buildTiles(report, mode), [report, mode]);
	const [activeKey, setActiveKey] = React.useState(() => defaultTileKey(tiles));
	const activeTile = tiles.find((tile) => tile.key === activeKey) || tiles[0];
	const listKey = activeTile.key === "toReview" ? "assigned" : activeTile.key;
	const actionsEnabled = mode === "done";

	const columns = [
		{
			title: "Card",
			key: "card",
			width: 92,
			render: (_, item) => (
				<div className="xc-card-id">
					<span className="xc-serial">{item.seriale ?? "?"}</span>
					{item.page != null && <span className="xc-page">pag. {item.page}</span>}
				</div>
			)
		},
		{
			title: "Prodotto e foto",
			key: "product",
			render: (_, item) => <ProductCell item={item}/>
		},
		{
			title: "Codice",
			key: "code",
			width: 210,
			render: (_, item) => renderCode(item)
		}
	];

	if (actionsEnabled)
		columns.push({
			title: "",
			key: "actions",
			width: 180,
			align: "right",
			render: (_, item) => renderActions(item)
		});

	function renderCode(item)
	{
		if (listKey === "assigned")
		{
			const options = [item.codice, ...(item.alternative || [])].filter(Boolean);
			const chip = <CodeChip code={item.codice}/>;
			if (!actionsEnabled || options.length < 2)
				return (
					<div className="xc-code-cell">
						{chip}
						{options.length > 1 && <span className="xc-alt-count">+{options.length - 1} nell'Excel</span>}
						{item.replacedFrom && <span className="xc-code-note">prima: <span className="xc-mono">{item.replacedFrom}</span></span>}
					</div>
				);
			return (
				<Dropdown
					trigger={["click"]}
					disabled={savingKey === itemKey(item)}
					menu={{
						selectedKeys: [item.codice],
						items: options.map((code) => ({ key: code, label: <span className="xc-mono">{code}</span> })),
						onClick: ({ key }) => key !== item.codice && onApplyCode(item, key, "assigned")
					}}
				>
					<button type="button" className="xc-code-picker" aria-label={`Scegli il codice per il seriale ${item.seriale}`}>
						{chip}
						<span className="xc-alt-count">{options.length} codici</span>
						<DownOutlined/>
					</button>
				</Dropdown>
			);
		}
		if (listKey === "alreadySet")
			return (
				<div className="xc-code-cell is-stacked">
					<CodeChip code={item.codiceAttuale}/>
					{item.codice && item.codice !== item.codiceAttuale && (
						<span className="xc-code-note">Excel: <span className="xc-mono">{item.codice}</span></span>
					)}
				</div>
			);
		return item.codiceAttuale
			? <div className="xc-code-cell is-stacked"><CodeChip code={item.codiceAttuale}/><span className="xc-code-note">già presente</span></div>
			: null;
	}

	function renderActions(item)
	{
		const saving = savingKey === itemKey(item);
		const open = onOpenCard && (
			<Button size="small" type="text" icon={<ExportOutlined/>} onClick={() => onOpenCard(item)}>
				Apri card
			</Button>
		);

		let primary = null;
		if (listKey === "assigned" && needsReview(item))
			primary = (
				<Tooltip title="Il codice è corretto: togli la card da quelle da ricontrollare">
					<Button size="small" icon={<CheckOutlined/>} onClick={() => onMarkReviewed(item)} aria-label="Codice corretto"/>
				</Tooltip>
			);
		else if (listKey === "alreadySet" && item.codice && item.codice !== item.codiceAttuale)
			primary = (
				<Popconfirm
					title="Sostituire il codice attuale con quello dell'Excel?"
					description={<span className="xc-mono">{item.codiceAttuale} → {item.codice}</span>}
					okText="Sostituisci"
					cancelText="Annulla"
					onConfirm={() => onApplyCode(item, item.codice, "alreadySet")}
				>
					<Button size="small" icon={<SwapOutlined/>} loading={saving}>Sostituisci</Button>
				</Popconfirm>
			);

		return <div className="xc-actions">{primary}{open}</div>;
	}

	const replaceable = activeTile.key === "alreadySet" ? activeTile.items.filter(isReplaceable) : [];
	const replaceableIds = replaceable.map(itemKey);
	const selectedCount = selectedIds.filter((id) => replaceableIds.includes(id)).length;
	// La casella in testa di Ant Design agisce solo sulla pagina visibile: qui seleziona
	// e deseleziona tutte le card sostituibili, in ogni pagina dell'elenco.
	const allSelected = replaceable.length > 0 && selectedCount === replaceable.length;
	const rowSelection = replaceable.length && onSelectIds
		? {
			selectedRowKeys: selectedIds,
			onChange: (keys) => onSelectIds(keys.map(String)),
			getCheckboxProps: (item) => ({ disabled: !isReplaceable(item) || bulkReplacing }),
			columnTitle: (
				<Checkbox
					checked={allSelected}
					indeterminate={selectedCount > 0 && !allSelected}
					disabled={bulkReplacing}
					onChange={() => onSelectIds(allSelected ? [] : replaceableIds)}
					aria-label={allSelected ? "Deseleziona tutte" : "Seleziona tutte le card da sostituire"}
				/>
			)
		}
		: undefined;

	const emptyText = {
		assigned: mode === "preview" ? "Nessun codice da assegnare" : "Nessun codice assegnato",
		toReview: "Niente da ricontrollare",
		notFound: "Tutte le foto principali hanno un codice presente nell'Excel",
		noPhoto: "Tutte le card hanno almeno una foto"
	}[activeTile.key] || "Nessun prodotto";

	return (
		<div className="xc-report">
			<div className="xc-tiles" role="tablist" aria-label="Esito dell'abbinamento">
				{tiles.map((tile) => (
					<button
						key={tile.key}
						type="button"
						role="tab"
						aria-selected={tile.key === activeTile.key}
						className={`xc-tile is-${tile.tone}${tile.key === activeTile.key ? " is-active" : ""}${tile.items.length ? "" : " is-zero"}`}
						onClick={() => setActiveKey(tile.key)}
						title={tile.hint}
					>
						<span className="xc-tile-count">{tile.items.length}</span>
						<span className="xc-tile-label">{tile.label}</span>
					</button>
				))}
			</div>

			{activeTile.key === "alreadySet" && (
				<div className={`xc-select-bar ${replaceable.length ? "is-attention" : "is-neutral"}`} role="status">
					<span className="xc-select-icon" aria-hidden="true">
						{replaceable.length ? <SwapOutlined/> : <CheckOutlined/>}
					</span>
					<span className="xc-select-body">
						<span className="xc-select-title">
							{!replaceable.length
								? "Nessun codice da sostituire"
								: selectedCount
									? `${selectedCount} di ${replaceable.length} selezionati`
									: `${replaceable.length} ${replaceable.length === 1 ? "codice diverso" : "codici diversi"} da quelli dell'Excel`}
						</span>
						<span className="xc-select-desc">
							{!replaceable.length
								? "Queste card hanno già lo stesso codice dell'Excel."
								: selectedCount
									? (mode === "preview"
										? "Verranno sostituiti con il codice dell'Excel quando importi."
										: "Premi \"Sostituisci selezionati\" per salvarli.")
									: "L'import non sovrascrive i codici presenti: seleziona quelli da sostituire."}
						</span>
					</span>
					{replaceable.length > 0 && onSelectIds && (
						<span className="xc-select-actions">
							{selectedCount > 0 && (
								<Button size="small" type="text" disabled={bulkReplacing} onClick={() => onSelectIds([])}>
									Nessuno
								</Button>
							)}
							{selectedCount < replaceable.length && (
								<Button size="small" disabled={bulkReplacing} onClick={() => onSelectIds(replaceableIds)}>
									Seleziona tutti ({replaceable.length})
								</Button>
							)}
							{mode === "done" && onBulkReplace && selectedCount > 0 && (
								<Popconfirm
									title={`Sostituire ${selectedCount} ${selectedCount === 1 ? "codice" : "codici"} con quelli dell'Excel?`}
									okText="Sostituisci"
									cancelText="Annulla"
									onConfirm={() => onBulkReplace(activeTile.items.filter((item) => selectedIds.includes(itemKey(item)) && isReplaceable(item)))}
								>
									<Button size="small" type="primary" icon={<SwapOutlined/>} loading={bulkReplacing}>
										Sostituisci selezionati
									</Button>
								</Popconfirm>
							)}
						</span>
					)}
				</div>
			)}

			<Table
				size="small"
				className="xc-table"
				rowKey={itemKey}
				rowSelection={rowSelection}
				columns={columns}
				dataSource={activeTile.items}
				pagination={activeTile.items.length > PAGE_SIZE ? {
					pageSize: PAGE_SIZE,
					size: "small",
					showSizeChanger: false,
					showTotal: (total, [from, to]) => `${from}-${to} di ${total}`
				} : false}
				locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText}/> }}
			/>
		</div>
	);
}

function FileSummary({ file, info })
{
	return (
		<div className="xc-file">
			<div className="xc-file-main">
				<FileExcelOutlined className="xc-file-icon"/>
				<div className="xc-file-text">
					<span className="xc-file-name">{file?.name}</span>
					{info && (
						<span className="xc-file-meta">
							{info.codes} codici articolo letti
							{info.withAlternatives ? `, ${info.withAlternatives} presenti su più righe` : ""}
						</span>
					)}
				</div>
				{info?.format && (
					<Tag className="xc-format">
						{FORMAT_LABELS[info.format] || info.format}
					</Tag>
				)}
			</div>
		</div>
	);
}

function ReportSkeleton()
{
	return (
		<div className="xc-report" aria-busy="true">
			<div className="xc-tiles">
				{[0, 1, 2].map((i) => (
					<div key={i} className="xc-tile is-skeleton">
						<Skeleton.Button active size="small" style={{ width: 40 }}/>
						<Skeleton.Input active size="small" style={{ width: 90, minWidth: 90 }}/>
					</div>
				))}
			</div>
			<Skeleton active title={false} paragraph={{ rows: 4 }}/>
		</div>
	);
}

/**
 * Import dei codici clienti in due tempi: scelto il file parte un'anteprima (nessuna
 * scrittura), poi "Importa" salva. Il resoconto finale resta finché la pagina è aperta,
 * così si può andare su una card e tornare a quelle da ricontrollare.
 */
export default function UploadExcelCodesModal({ open, onClose, flyerId, onComplete, onOpenCard }) {
	const { message } = AntdApp.useApp();
	const [file, setFile] = React.useState(null);
	const [phase, setPhase] = React.useState("pick"); // pick | analyzing | preview | importing | done
	const [preview, setPreview] = React.useState(null);
	const [result, setResult] = React.useState(null);
	const [error, setError] = React.useState(null);
	const [savingKey, setSavingKey] = React.useState(null);
	const [pickerKey, setPickerKey] = React.useState(0);
	// Card "già con codice" scelte per la sostituzione (anteprima: le sostituisce l'import;
	// resoconto: "Sostituisci selezionati").
	const [replaceIds, setReplaceIds] = React.useState([]);
	const [bulkReplacing, setBulkReplacing] = React.useState(false);
	// Il resoconto si ricostruisce da zero a ogni import: la chiave azzera anche la
	// scheda selezionata nel componente del resoconto.
	const [reportKey, setReportKey] = React.useState(0);

	const reset = React.useCallback(() => {
		setFile(null);
		setPhase("pick");
		setPreview(null);
		setResult(null);
		setError(null);
		setSavingKey(null);
		setReplaceIds([]);
		setBulkReplacing(false);
		setPickerKey((k) => k + 1);
	}, []);

	React.useEffect(() => {
		if (!open && phase !== "done")
			reset();
	}, [open, phase, reset]);

	React.useEffect(() => {
		reset();
	}, [flyerId, reset]);

	const send = React.useCallback(async (selectedFile, dryRun, replaceCardIds = []) => {
		const formData = new FormData();
		formData.append("flyerId", flyerId);
		if (dryRun)
			formData.append("dryRun", "1");
		if (replaceCardIds.length)
			formData.append("replaceCardIds", JSON.stringify(replaceCardIds));
		formData.append("excel", selectedFile, selectedFile.name);
		const { data } = await api.post("/api/ai/excel-codes", formData);
		return data;
	}, [flyerId]);

	const analyze = React.useCallback(async (selectedFile) => {
		if (!flyerId)
			return;
		setFile(selectedFile);
		setError(null);
		setPhase("analyzing");
		try {
			const data = await send(selectedFile, true);
			setPreview(data);
			setReplaceIds([]);
			setReportKey((k) => k + 1);
			setPhase("preview");
		} catch (err) {
			setError({
				message: err?.response?.data?.error || "Il file non si può leggere.",
				headers: err?.response?.data?.file?.headers || []
			});
			setPreview(null);
			setPhase("pick");
		}
	}, [flyerId, send]);

	const runImport = async () => {
		if (!file || phase !== "preview")
			return;
		setPhase("importing");
		try {
			const data = await send(file, false, replaceIds);
			setResult(data);
			setReplaceIds([]);
			setReportKey((k) => k + 1);
			setPhase("done");
			onComplete?.(data);
		} catch (err) {
			message.error(err?.response?.data?.error || "Errore durante l'import dei codici.");
			setPhase("preview");
		}
	};

	const updateReport = React.useCallback((updater) => {
		setResult((prev) => prev ? { ...prev, report: updater(prev.report) } : prev);
	}, []);

	const applyCode = React.useCallback(async (item, code, fromList) => {
		const key = itemKey(item);
		setSavingKey(key);
		try {
			await api.patch(`/api/cards/${item.cardId}`, { "ai.codice_cliente": code });
			updateReport((report) => {
				const next = { ...report };
				if (fromList === "alreadySet")
					next.alreadySet = report.alreadySet.map((it) => itemKey(it) === key ? { ...it, codiceAttuale: code } : it);
				else
					next.assigned = report.assigned.map((it) => itemKey(it) === key ? { ...it, codice: code, reviewed: true } : it);
				return next;
			});
			message.success(`Codice ${code} salvato sul seriale ${item.seriale}`);
		} catch (err) {
			message.error(err?.response?.data?.error || "Codice non salvato.");
		} finally {
			setSavingKey(null);
		}
	}, [message, updateReport]);

	// Sostituzione in blocco dal resoconto: una richiesta per card con il normale
	// salvataggio, così ogni card si aggiorna anche nella pagina.
	const bulkReplace = React.useCallback(async (items) => {
		if (!items.length)
			return;
		setBulkReplacing(true);
		const done = [];
		for (const item of items)
		{
			try {
				await api.patch(`/api/cards/${item.cardId}`, { "ai.codice_cliente": item.codice });
				done.push(itemKey(item));
			} catch {
				// Le card non salvate restano selezionate per riprovare.
			}
		}
		updateReport((report) => ({
			...report,
			alreadySet: report.alreadySet.map((it) => done.includes(itemKey(it)) ? { ...it, codiceAttuale: it.codice } : it)
		}));
		setReplaceIds((prev) => prev.filter((id) => !done.includes(id)));
		setBulkReplacing(false);
		if (done.length === items.length)
			message.success(`${done.length} ${done.length === 1 ? "codice sostituito" : "codici sostituiti"}`);
		else
			message.warning(`${done.length} sostituiti, ${items.length - done.length} non salvati: riprova`);
	}, [message, updateReport]);

	const markReviewed = React.useCallback((item) => {
		const key = itemKey(item);
		updateReport((report) => ({
			...report,
			assigned: report.assigned.map((it) => itemKey(it) === key ? { ...it, reviewed: true } : it)
		}));
	}, [updateReport]);

	const draggerProps = {
		beforeUpload: (selectedFile) => {
			void analyze(selectedFile);
			return false;
		},
		multiple: false,
		directory: false,
		showUploadList: false,
		accept: ".xlsx,.xls,.xlsm"
	};

	const busy = phase === "analyzing" || phase === "importing";
	const toAssign = preview?.toAssign ?? 0;
	const toReplace = (preview?.report?.alreadySet || []).filter((item) => isReplaceable(item) && replaceIds.includes(itemKey(item))).length;
	let importLabel = "Nessun codice da importare";
	if (toAssign && toReplace)
		importLabel = `Importa ${toAssign} ${toAssign === 1 ? "codice" : "codici"} e sostituiscine ${toReplace}`;
	else if (toAssign)
		importLabel = `Importa ${toAssign} ${toAssign === 1 ? "codice" : "codici"}`;
	else if (toReplace)
		importLabel = `Sostituisci ${toReplace} ${toReplace === 1 ? "codice" : "codici"}`;

	let footer;
	if (phase === "done")
		footer = [
			<Button key="again" onClick={reset}>Carica un altro file</Button>,
			<Button key="close" type="primary" onClick={onClose}>Fine</Button>
		];
	else if (phase === "preview" || phase === "importing")
		footer = [
			<Button key="change" onClick={reset} disabled={busy}>Cambia file</Button>,
			<Button key="import" type="primary" onClick={runImport} loading={phase === "importing"} disabled={!toAssign && !toReplace}>
				{importLabel}
			</Button>
		];
	else
		footer = [<Button key="close" onClick={onClose} disabled={busy}>Annulla</Button>];

	return (
		<Modal
			open={open}
			onCancel={busy ? undefined : onClose}
			title="Carica Codici Clienti"
			width={MODAL_WIDTH}
			maskClosable={!busy}
			closable={!busy}
			className="sis-modal xc-modal"
			footer={footer}
			destroyOnHidden
		>
			{phase === "pick" && (
				<div className="xc-pick">
					<Upload.Dragger {...draggerProps} key={pickerKey} className="xc-dragger">
						<p className="ant-upload-drag-icon"><FileExcelOutlined/></p>
						<p className="ant-upload-text">Trascina il file Excel o fai clic per sceglierlo</p>
						<p className="ant-upload-hint">Il codice si legge dal nome della foto principale di ogni prodotto, ad esempio 6095178.jpg</p>
						<p className="ant-upload-hint xc-hint-format">Excel con le colonne <span className="xc-mono">PXARTP</span> e <span className="xc-mono">codice</span> (padre-figlio, es. 0043035-43035)</p>
					</Upload.Dragger>

					{error && (
						<Alert
							type="error"
							showIcon
							className="xc-error"
							message={error.message}
							description={error.headers.length
								? <>Colonne trovate nel file: {error.headers.map((h) => <Tag key={h} className="xc-mono">{h}</Tag>)}</>
								: null}
						/>
					)}
				</div>
			)}

			{phase === "analyzing" && (
				<>
					<FileSummary file={file} info={preview?.file}/>
					<Text type="secondary" className="xc-status">Analisi del file in corso, nessun dato viene salvato</Text>
					<ReportSkeleton/>
				</>
			)}

			{(phase === "preview" || phase === "importing") && preview && (
				<>
					<FileSummary file={file} info={preview.file}/>
					<Text type="secondary" className="xc-status">
						Anteprima su {preview.total} prodotti del volantino: niente è ancora stato salvato
					</Text>
					<ImportReport
						key={reportKey}
						result={preview}
						mode="preview"
						selectedIds={replaceIds}
						onSelectIds={phase === "importing" ? undefined : setReplaceIds}
					/>
				</>
			)}

			{phase === "done" && result && (
				<>
					<FileSummary file={file} info={result.file}/>
					<ImportReport
						key={reportKey}
						result={result}
						mode="done"
						onApplyCode={applyCode}
						onMarkReviewed={markReviewed}
						onOpenCard={onOpenCard}
						savingKey={savingKey}
						selectedIds={replaceIds}
						onSelectIds={setReplaceIds}
						onBulkReplace={bulkReplace}
						bulkReplacing={bulkReplacing}
					/>
				</>
			)}
		</Modal>
	);
}
